import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ExternalLink, RefreshIcon } from 'assets/icons';
import Button from 'components/button';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import {
  INVOICE_LIST_STALE,
  fillText,
} from 'components/invoiceImport/invoiceImportText';
import InvoiceStatusBadge from 'components/invoiceList/InvoiceStatusBadge';
import { downloadFile } from 'components/invoiceList/useInvoiceExport';
import {
  INVOICE_MAIL_SENT,
  invoiceSendMailText,
} from 'components/invoiceSendMail/invoiceSendMailText';
import {
  INVOICE_LITIGATION_MODAL,
  INVOICE_REMINDER_MODAL,
  invoiceRemindersText,
} from 'components/invoiceReminders/invoiceRemindersText';
import useInvoiceReminderChanged from 'components/invoiceReminders/useInvoiceReminderChanged';
import useModal from 'components/modal/useModal';
import PillTabs from 'components/tabs/PillTabs';
import Tooltip from 'components/tooltips/Tooltips';
import { isSendableInvoiceStatus } from 'constants/invoiceSending';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import InvoiceCommentsTab from './InvoiceCommentsTab';
import InvoiceDetailInformation from './InvoiceDetailInformation';
import InvoiceHistoryTab from './InvoiceHistoryTab';
import InvoicePdfPreview, { DownloadIcon } from './InvoicePdfPreview';
import { companyLabelOf, toStatusBadgeInvoice } from './invoiceDetailText';
import { useInvoiceDetail, useInvoicePdf } from './useInvoiceDetail';
import useInvoiceComments from './useInvoiceComments';
import useInvoiceCompany from './useInvoiceCompany';
import useInvoiceDetailImport from './useInvoiceDetailImport';

const RELOAD_FAILED_TOAST_ID = 'invoice-detail-reload-failed';

// The statuses shown of an invoice an admin may remind by hand, late or not
// ("late" comes on top of a status). The api decides the rest (no due
// date…) and the modal shows its refusal.
const REMINDABLE_INVOICE_STATUSES = ['pending', 'partially_paid'];

const isRemindableInvoice = (invoice) =>
  Boolean(invoice) &&
  invoice.kind !== 'credit_note' &&
  invoice.importStatus === 'imported' &&
  REMINDABLE_INVOICE_STATUSES.includes(invoice.status);

const isInLitigation = (invoice) => Boolean(invoice?.litigation);

// Litigation takes a late invoice only (409 INVOICE_NOT_LATE otherwise).
const canStartLitigationOf = (invoice) =>
  isRemindableInvoice(invoice) &&
  !isInLitigation(invoice) &&
  invoice.lateDays > 0;

// The main action of the footer for each kind of invoice.
const mainActionOf = (invoice) => {
  if (invoice.status === 'error') return 'retry';
  if (invoice.importStatus !== 'imported') return 'import';
  return invoice.pennylane?.url ? 'pennylane' : null;
};

const InvoiceDetailView = ({ number, onClose }) => {
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle: toggleModal } = useModal();
  const text = useCallback(
    (key, fallback) => language.getValue(`invoiceDetail.${key}`, fallback),
    [language],
  );
  // The provider rebuilds `language` on every render, so `text` changes with
  // every context update: effects read it through a ref.
  const textRef = useRef(text);
  textRef.current = text;
  const panelRef = useRef(null);
  const [tab, setTab] = useState('information');
  const [categories, setCategories] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const detail = useInvoiceDetail(number);
  const pdf = useInvoicePdf(number);
  // A comment deleted writes a « Commentaire supprimé » line in the history.
  const onCommentDeleted = useCallback(
    () => setHistoryKey((key) => key + 1),
    [],
  );
  const comments = useInvoiceComments(number, onCommentDeleted);
  const { invoice } = detail;
  const company = useInvoiceCompany(invoice?.company?.id);

  useEffect(() => {
    ApiDatabase.getPennyLaneCategories(
      {},
      (data) => setCategories(data?.items ?? []),
      (error) => {
        if (error?.name === 'CanceledError') return;
        // Without the list, a category is named by its number.
        setCategories([]);
      },
    );
  }, []);

  // A reload that fails over an invoice already shown keeps it, and says so.
  useEffect(() => {
    if (detail.failure && invoice) {
      toast.error(
        textRef.current(
          'reloadFailed',
          'La facture n’a pas pu être relue, rechargez-la.',
        ),
        { toastId: RELOAD_FAILED_TOAST_ID },
      );
    }
  }, [detail.failure, invoice]);

  // The focus moves into the panel when it opens.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const { reload: reloadDetail, replace: replaceInvoice } = detail;

  // The modal of the category and the term answers with the invoice as the
  // api wrote it, on a success and on the refusals that still wrote part of
  // it, in the same return that tells the month's list to read itself again
  // (the list reacts in this same commit, before the reset). When the api
  // could not read the invoice back after writing, the answer holds the
  // fields sent only, without `status`: the panel reads the invoice again
  // rather than keep a stale status and lateness.
  useEffect(() => {
    if (modalDataReturn?.type !== INVOICE_LIST_STALE) return;
    if (!('invoice' in modalDataReturn)) return;
    if (Number(modalDataReturn.number) === number) {
      if (modalDataReturn.invoice?.status) {
        replaceInvoice(modalDataReturn.invoice);
      } else {
        reloadDetail();
      }
    }
    setModalDataReturn({});
  }, [
    modalDataReturn,
    number,
    replaceInvoice,
    reloadDetail,
    setModalDataReturn,
  ]);

  // A mail sent writes a « Facture envoyée par mail » line in the history.
  // The page's list reacts to the same return: nobody resets it.
  useEffect(() => {
    if (modalDataReturn?.type !== INVOICE_MAIL_SENT) return;
    if ((modalDataReturn.invoiceNumbers ?? []).map(Number).includes(number)) {
      setHistoryKey((key) => key + 1);
    }
  }, [modalDataReturn, number]);

  // A reminder sent writes a line in the history, and a refusal may come
  // with a status the api has just written (paid in Pennylane meanwhile). A
  // move into or out of litigation fires the same return, and writes its own
  // line; the detail read again carries the new litigation state.
  useInvoiceReminderChanged((changedNumber) => {
    if (changedNumber !== number) return;
    reloadDetail();
    setHistoryKey((key) => key + 1);
  });

  const openInformationEditor = () => {
    setModalData({ invoice, categories });
    toggleModal('invoice_information');
  };
  // Confirming an appendix can close the invoice's ERR_PENNYLANE_ANNEXE: the
  // invoice, its history and the month's list are read again.
  const onAppendixConfirmed = useCallback(() => {
    reloadDetail();
    setHistoryKey((key) => key + 1);
    setModalDataReturn({ type: INVOICE_LIST_STALE });
  }, [reloadDetail, setModalDataReturn]);
  const { reload: reloadPdf, blob } = pdf;
  // At the end of a run the invoice has changed; its PDF only matters if it
  // could not be shown (an import was running, BeSTT failed).
  const onRunFinished = useCallback(() => {
    reloadDetail();
    if (!blob) reloadPdf();
    setHistoryKey((key) => key + 1);
  }, [reloadDetail, reloadPdf, blob]);
  const actions = useInvoiceDetailImport(invoice, onRunFinished);

  let title = fillText(text('titleLoading', 'N° {number}'), { number });
  if (invoice) {
    title = fillText(text('title', 'N° {number} · {company}'), {
      number: invoice.number,
      company: companyLabelOf(text, invoice),
    });
  }

  let body;
  if (!invoice && detail.failure) {
    body = (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-sm text-gray-700"
      >
        <div>
          {detail.failure === 'notFound'
            ? text('notFound', 'Cette facture est introuvable.')
            : text('loadFailed', 'La facture n’a pas pu être chargée.')}
        </div>
        {detail.failure === 'failed' && (
          <Button color="SECONDARY" onClick={reloadDetail}>
            {text('reload', 'Recharger')}
          </Button>
        )}
      </div>
    );
  } else if (!invoice) {
    body = (
      <div role="status" className="py-16 text-center text-sm text-gray-500">
        {text('loading', 'Chargement de la facture…')}
      </div>
    );
  } else if (tab === 'information') {
    body = (
      <InvoiceDetailInformation
        invoice={invoice}
        categories={categories}
        company={company}
        text={text}
        onEdit={openInformationEditor}
        onAppendixConfirmed={onAppendixConfirmed}
      />
    );
  } else if (tab === 'history') {
    body = (
      <InvoiceHistoryTab
        invoice={invoice}
        categories={categories}
        refreshKey={historyKey}
      />
    );
  } else {
    body = <InvoiceCommentsTab comments={comments} />;
  }

  const mainAction = invoice ? mainActionOf(invoice) : null;
  const footer = (key, fallback) => text(`footer.${key}`, fallback);
  const downloadLabel = footer('download', 'Télécharger le PDF');
  const mailText = invoiceSendMailText(language);
  const canSendByMail =
    Boolean(invoice) && isSendableInvoiceStatus(invoice.status);
  // One modal at a time in the Admin: it opens over the panel, as 1275's does.
  const openMail = () => {
    setModalData({ invoiceNumbers: [number] });
    toggleModal('invoice_send_mail');
  };
  const inLitigation = isInLitigation(invoice);
  // The api refuses any reminder of an invoice in litigation.
  const canRemind = isRemindableInvoice(invoice) && !inLitigation;
  const openReminder = () => {
    setModalData({ number });
    toggleModal(INVOICE_REMINDER_MODAL);
  };
  const canStartLitigation = canStartLitigationOf(invoice);
  const openLitigation = (action) => {
    setModalData({ number, action });
    toggleModal(INVOICE_LITIGATION_MODAL);
  };
  const remindersText = invoiceRemindersText(language);
  let mainButton = null;
  if (mainAction === 'pennylane') {
    mainButton = (
      <a
        href={invoice.pennylane.url}
        target="_blank"
        rel="noopener noreferrer"
        className="h-12 flex items-center gap-2 px-4 rounded-md bg-yellow-500 text-white hover:bg-yellow-600"
      >
        {footer('viewInPennylane', 'Voir dans Pennylane')}
        <ExternalLink wh={18} color="#FFFFFF" />
      </a>
    );
  } else if (mainAction) {
    const isRetry = mainAction === 'retry';
    let label = isRetry
      ? footer('retry', 'Réessayer l’import')
      : footer('import', 'Importer dans Pennylane');
    if (actions.isRunning) label = footer('running', 'Import en cours…');
    mainButton = (
      <Button
        size="SM"
        color={actions.isBusy ? 'DISABLED' : 'PRIMARY'}
        disabled={actions.isBusy}
        onClick={isRetry ? actions.retryInvoice : actions.importInvoice}
      >
        {isRetry && !actions.isRunning && (
          <RefreshIcon wh={18} color="currentColor" />
        )}
        {label}
      </Button>
    );
  }

  return (
    <div className="flex flex-1 min-h-0">
      <InvoicePdfPreview pdf={pdf} text={text} number={number} />
      <div
        ref={panelRef}
        role="region"
        aria-label={title}
        tabIndex={-1}
        className="flex flex-col w-1/2 min-w-[420px] min-h-0 border-l border-gray-100 outline-none"
      >
        <SlideOverHeader
          hide={onClose}
          closeLabel={language.getValue('invoiceImport.close', 'Fermer')}
          extraActions={
            invoice && (
              <InvoiceStatusBadge invoice={toStatusBadgeInvoice(invoice)} />
            )
          }
        >
          <span className="block text-xs font-normal uppercase tracking-wide text-gray-500">
            {text('kicker', 'Facture client')}
          </span>
          <span className="block ft-lg">{title}</span>
        </SlideOverHeader>
        <div className="flex-shrink-0 px-5 pb-2">
          <PillTabs
            tabs={[
              {
                id: 'information',
                label: text('tabInformation', 'Informations'),
              },
              { id: 'history', label: text('tabHistory', 'Historique') },
              {
                id: 'comments',
                label: comments.count
                  ? fillText(
                      language.getValue(
                        'invoiceComments.tabWithCount',
                        'Commentaires ({count})',
                      ),
                      { count: comments.count },
                    )
                  : text('tabComments', 'Commentaires'),
              },
            ]}
            activeTab={tab}
            onChange={setTab}
          />
        </div>
        {/* The history runs from edge to edge, its month headers stuck to the
            top: it pads its own rows. */}
        <SlideOverBody className={invoice && tab === 'history' ? '!p-0' : ''}>
          {/* Keyed by tab so the fade replays on each switch; each tab is its
              own component, so it remounted anyway. */}
          <div key={invoice ? tab : 'state'} className="motion-safe:animate-contentIn">
            {body}
          </div>
        </SlideOverBody>
        <div className="flex-shrink-0 border-t border-gray-100">
          <SlideOverFooter>
            {/* The PDF and its mail go together, closer than the other actions. */}
            <div className="flex items-center gap-2">
              <Tooltip text={downloadLabel} placement="top">
                <Button
                  size="ICON"
                  color="SECONDARY"
                  aria-label={downloadLabel}
                  disabled={!blob}
                  className="disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => downloadFile(blob, `facture-${number}.pdf`)}
                >
                  <DownloadIcon />
                </Button>
              </Tooltip>
              {canSendByMail && (
                <Button size="SM" color="SECONDARY" onClick={openMail}>
                  {mailText('open')}
                </Button>
              )}
            </div>
            {canRemind && (
              <Button size="SM" color="SECONDARY" onClick={openReminder}>
                {remindersText('detail.remind')}
              </Button>
            )}
            {canStartLitigation && (
              <Button
                size="SM"
                color="SECONDARY"
                onClick={() => openLitigation('start')}
              >
                {remindersText('litigation.start')}
              </Button>
            )}
            {inLitigation && (
              <Button
                size="SM"
                color="SECONDARY"
                onClick={() => openLitigation('end')}
              >
                {remindersText('litigation.end')}
              </Button>
            )}
            {mainButton}
          </SlideOverFooter>
        </div>
      </div>
    </div>
  );
};

/**
 * The detail of a client invoice (JBWEB-1274, mockups 3.1 to 3.6), the
 * slide-over `get_invoice`: its PDF on the left, its information on the
 * right, and what can be done with it in the footer. `objID` is its BeSTT
 * number; each number mounts the view anew, tab and zoom included.
 */
const InvoiceDetailPanel = () => {
  const { objID, removeAllParameters } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const number = Number(objID);
  const close = () => {
    toggleSlideOver();
    removeAllParameters?.();
  };
  if (!Number.isInteger(number) || number <= 0) return null;
  return <InvoiceDetailView key={number} number={number} onClose={close} />;
};

export default InvoiceDetailPanel;
